/* eslint-disable react/no-unused-state */
import { autobind } from 'core-decorators';
import debounce from 'lodash.debounce';
import React, { PureComponent } from 'react';
import OrderStatusEnum from 'const/OrderStatusEnum';
import { processing } from 'decorators';
import * as mainApi from 'services/api/main';
import { withErrorNotification } from 'storage/NotificationsContext';
import mainWithContext from 'with/withContext';

type State = {
  pendingOrders: WoaRequest[];
  accountId?: number;
  isFetching: boolean;
  keyword?: string;
  error?: Error;
  numOfResults?: number;
  offset: number;
  totalOrders: number;
  sortBy?: string | null;
  sortDirection?: string | null;
  getPendingOrdersList: (
    accountId: number,
    params?: {
      keyword?: string;
      sortBy?: string | null;
      sortDirection?: string | null;
      isSortingUpdate?: boolean;
    },
    newOffset?: number | undefined,
  ) => void;
};

type PendingOrdersContextInterface = State & {
  reloadPendingOrders: () => void;
  updateOrdersList: () => void;
  updateSorting: (sortBy: string | null, sortDirection: string | null) => void;
};

const defaultValue = {
  pendingOrders: [],
  isFetching: true,
  totalOrders: 0,
  offset: 0,
  getPendingOrdersList: () => {},
  reloadPendingOrders: () => {},
  updateOrdersList: () => {},
  updateSorting: () => {},
};

const PendingOrdersContext = React.createContext<PendingOrdersContextInterface>(defaultValue);

function withProvider(WrappedComponent) {
  @autobind
  class PendingOrdersProviderWrapper extends PureComponent<any, State> {
    state: State = {
      pendingOrders: [],
      isFetching: true,
      totalOrders: 0,
      numOfResults: 15,
      offset: 0,
      getPendingOrdersList: debounce(
        (accountId, params, newOffset) => this._getPendingOrdersList(accountId, params, newOffset),
        100,
      ),
    };

    @withErrorNotification
    @processing('isFetching')
    async _getPendingOrdersList(
      accountId,
      {
        isSortingUpdate,
        keyword,
        sortBy,
        sortDirection,
      }: { keyword?: string; sortBy?: string | null; sortDirection?: string | null; isSortingUpdate?: boolean } = {},
      newOffset = undefined,
    ) {
      let { numOfResults, offset } = this.state;
      const newNumOfResults = isSortingUpdate ? this.state.pendingOrders.length : numOfResults;

      if (newOffset !== undefined) {
        offset = newOffset;
      }

      if (isSortingUpdate || this.state.keyword !== keyword) {
        offset = 0;
      }

      this.setState({
        accountId,
        keyword,
        sortBy,
        sortDirection,
        numOfResults,
        offset,
      });

      try {
        const [pendingOrders, totalOrders] = await Promise.all([
          mainApi.getPendingOrders(accountId, keyword, newNumOfResults, offset, sortBy, sortDirection),
          mainApi.getRequestCount(accountId, OrderStatusEnum.PENDING),
        ]);

        this.setState(state => ({
          error: null,
          ...state,
          pendingOrders: offset === 0 ? pendingOrders : state.pendingOrders.concat(pendingOrders),
          totalOrders: totalOrders > 0 ? totalOrders : 0,
        }));
      } catch (error) {
        this.setState({ error, pendingOrders: [] });

        throw error;
      }
    }

    reloadPendingOrders(isSortingUpdate = false) {
      const { accountId, getPendingOrdersList, keyword, sortBy, sortDirection } = this.state;

      if (accountId) {
        getPendingOrdersList(accountId, { keyword, sortBy, sortDirection, isSortingUpdate });
      }
    }

    updateOrdersList() {
      if (this.state.totalOrders > this.state.offset + this.state.numOfResults) {
        this.setState(state => ({ ...state, offset: state.pendingOrders.length }));
      }

      this.reloadPendingOrders();
    }

    updateSorting(sortBy: string | null, sortDirection: string | null) {
      this.setState({ sortBy, sortDirection }, () => {
        this.reloadPendingOrders(true);
      });
    }

    render() {
      return (
        <PendingOrdersContext.Provider
          value={{
            ...this.state,
            reloadPendingOrders: this.reloadPendingOrders,
            updateOrdersList: this.updateOrdersList,
            updateSorting: this.updateSorting,
          }}
        >
          <WrappedComponent {...this.props} />
        </PendingOrdersContext.Provider>
      );
    }
  }

  return PendingOrdersProviderWrapper;
}

export { PendingOrdersContextInterface };
export default {
  useContext: () => React.useContext(PendingOrdersContext),
  withProvider,
  withContext: mainWithContext(PendingOrdersContext),
};
